import { IKeystoreController } from '@ambire-common/interfaces/keystore'
import { IRequestsController } from '@ambire-common/interfaces/requests'
import { ISurveyController } from '@ambire-common/interfaces/survey'
import { ISwapAndBridgeController } from '@ambire-common/interfaces/swapAndBridge'
import { ITransferController } from '@ambire-common/interfaces/transfer'
import { getBenzinUrlParams } from '@ambire-common/utils/benzin'
import { AUTH_STATUS } from '@common/modules/auth/constants/authStatus'
import { ROUTES } from '@common/modules/router/constants/common'
import { getUiType } from '@common/utils/uiType'
import { FEATURES } from '@web/constants/features'

const { isRequestWindow, isMobileApp } = getUiType()

/**
 * Which of the two screens owns the pair currently in the controller. Same-chain
 * pairs belong on `/swap` (ZKX Trade); everything else on the legacy
 * `/swap-and-bridge` path, which renders the bridge form.
 */
const getSwapAndBridgeRoute = (swapAndBridgeState: ISwapAndBridgeController) => {
  const { fromChainId, toChainId } = swapAndBridgeState
  const isSameChainPair = !!fromChainId && !!toChainId && Number(fromChainId) === Number(toChainId)

  return isSameChainPair ? ROUTES.swap : ROUTES.swapAndBridge
}

const getInitialRoute = ({
  keystoreState,
  authStatus,
  requestsState,
  swapAndBridgeState,
  transferState,
  surveyState
}: {
  keystoreState: IKeystoreController
  authStatus?: AUTH_STATUS
  requestsState: IRequestsController
  swapAndBridgeState: ISwapAndBridgeController
  transferState: ITransferController
  surveyState?: ISurveyController
}) => {
  if (keystoreState.isReadyToStoreKeys && !keystoreState.isUnlocked) {
    return ROUTES.keyStoreUnlock
  }

  if (authStatus === AUTH_STATUS.NOT_AUTHENTICATED) {
    return ROUTES.getStarted
  }

  if ((isRequestWindow || isMobileApp) && requestsState.currentUserRequest) {
    const { currentUserRequest } = requestsState
    if (currentUserRequest.kind === 'dappConnect') return ROUTES.dappConnectRequest

    if (currentUserRequest.kind === 'walletAddEthereumChain') return ROUTES.addChain

    if (currentUserRequest.kind === 'walletWatchAsset') return ROUTES.watchAsset

    if (currentUserRequest.kind === 'ethGetEncryptionPublicKey')
      return ROUTES.getEncryptionPublicKeyRequest
    if (currentUserRequest.kind === 'ethDecrypt') return ROUTES.decryptRequest

    if (currentUserRequest.kind === 'calls') return ROUTES.signAccountOp

    if (
      currentUserRequest.kind === 'message' ||
      currentUserRequest.kind === 'typedMessage' ||
      currentUserRequest.kind === 'authorization-7702' ||
      currentUserRequest.kind === 'siwe'
    ) {
      return ROUTES.signMessage
    }

    // Swap and Bridge are separate screens now; resume onto the one matching the
    // pair actually in the controller, otherwise the screen the user lands on
    // re-points their destination (a same-chain pair reopened on the bridge
    // screen gets pushed back cross-chain).
    if (currentUserRequest.kind === 'swapAndBridge')
      return getSwapAndBridgeRoute(swapAndBridgeState)

    if (currentUserRequest.kind === 'transfer') {
      // GAS_TANK_ENABLED=false: the top-up route is not mounted, so a persisted
      // top-up form falls back to the plain Send screen.
      if (transferState.isTopUp && FEATURES.gasTankUiEnabled) {
        return ROUTES.topUpGasTank
      }

      return ROUTES.transfer
    }

    if (currentUserRequest.kind === 'benzin') {
      const link =
        ROUTES.benzin +
        getBenzinUrlParams({
          chainId: currentUserRequest.meta.chainId,
          isInternal: true,
          txnId: currentUserRequest.meta?.txnId, // can be undefined
          identifiedBy: currentUserRequest.meta?.identifiedBy
        })
      return link
    }

    if (currentUserRequest.kind === 'switchAccount') return ROUTES.switchAccount
  } else if (!isRequestWindow) {
    // TODO: Always redirects to Dashboard, which for initial extension load is okay, but
    // for other scenarios, ideally, it should be the last route before the keystore got locked.
    const hasSwapAndBridgePersistentSession = swapAndBridgeState.sessionIds.some(
      (id) => id === 'popup' || id === 'request-window'
    )

    if (hasSwapAndBridgePersistentSession) {
      return getSwapAndBridgeRoute(swapAndBridgeState)
    }
    if (transferState?.hasPersistedState) {
      // GAS_TANK_ENABLED=false: the top-up route is not mounted, so a persisted
      // top-up form falls back to the plain Send screen.
      if (transferState.isTopUp && FEATURES.gasTankUiEnabled) {
        return ROUTES.topUpGasTank
      }
      return ROUTES.transfer
    }
    if (surveyState && surveyState.hasPersistentState) return ROUTES.survey
    return ROUTES.dashboard
  }

  return null
}

export { getInitialRoute }
